import { round } from 'lodash-es';

/**
 * Standard decimal places for quantity display and input across warehouse
 * and financial documents.
 *
 * See docs/quantity_standard.md for full rationale.
 *
 * Ruby counterpart: QuantityPrecision::DECIMAL_PLACES
 * in extras/quantity_precision.rb
 */
export const QUANTITY_DECIMAL_PLACES = 4;

/**
 * Rounds a raw input value to the standard quantity precision.
 * Converts string input from <Input type="number"> to a numeric value
 * and rounds to QUANTITY_DECIMAL_PLACES using standard mathematical
 * rounding (half-up).
 *
 * @param {string|number|null|undefined} value - Raw input value
 * @returns {number} Rounded numeric quantity (NaN-safe: returns 0 for invalid input)
 */
export function roundQuantity(value) {
  const parsed = Number(value);

  if (Number.isNaN(parsed)) return 0;

  return round(parsed, QUANTITY_DECIMAL_PLACES);
}

/**
 * Formats a quantity for display in tables, forms, and reports.
 * Uses smart display: shows up to QUANTITY_DECIMAL_PLACES significant
 * decimal digits without trailing zeros.
 *
 * @param {number|null|undefined} quantity - The quantity value
 * @returns {string} Formatted quantity string (e.g. "5.5", "5.1234")
 */
export function formatQuantity(quantity) {
  if (quantity === null || quantity === undefined) return '';

  return String(roundQuantity(quantity));
}

/**
 * Formats a quantity with fixed decimal places for alignment in
 * comparison tables and reports where visual consistency matters.
 *
 * @param {number|null|undefined} quantity - The quantity value
 * @param {number} [decimalPlaces=QUANTITY_DECIMAL_PLACES] - Number of decimal places
 * @returns {string} Formatted quantity string with trailing zeros (e.g. "5.5000")
 */
export function formatQuantityFixed(quantity, decimalPlaces = QUANTITY_DECIMAL_PLACES) {
  if (quantity === null || quantity === undefined) return '';

  return roundQuantity(quantity).toFixed(decimalPlaces);
}

/**
 * Formats a quantity with unit label for display.
 *
 * @param {number|null|undefined} quantity - The quantity value
 * @param {string} unitLabel - The unit label (e.g. "kg", "l")
 * @returns {string} Formatted string (e.g. "5.5 kg")
 */
export function formatQuantityWithUnit(quantity, unitLabel) {
  if (quantity === null || quantity === undefined) return '';

  return `${formatQuantity(quantity)} ${unitLabel}`;
}
